'use client';
import { memo, useEffect, useRef, useState } from 'react';
import { Tooltip, useDisclosure, useMediaQuery, useOutsideClick } from '@chakra-ui/react';

import { useDebounce, useWindowResize } from 'shared/hooks';
import InterText from 'shared/InterText';

/**
 * Компонент `TooltipText` отображает текст с возможностью показа тултипа, если текст не умещается в контейнер.
 * Работает как по клику, так и при наведении.
 *
 * param {string} `title` - Текст заголовка, который будет отображаться.
 *
 * param {Object} `titleProps=null` - Дополнительные свойства компонента `Text`.
 *
 * param {Object} `tooltipProps=null` - Дополнительные свойства для компонента `Tooltip`.
 *
 * param {boolean} `isFullWidth=false` - Флаг, указывающий, должен ли тултип быть по ширине равен ширине текста.
 *
 * @example
 * <TooltipText title="Очень длинный заголовок, который не умещается в строке" />
 */
const TooltipText = ({ title, titleProps = null, tooltipProps = null, isFullWidth = false }) => {
  const titleRef = useRef(null);
  const [titleWidth, setTitleWidth] = useState();
  const [isTitleOverflow, setIsTitleOverflow] = useState(false);
  const { isOpen, onToggle, onClose, onOpen } = useDisclosure();
  const [isTablet] = useMediaQuery('(max-width: 1000px)');
  const { width } = useWindowResize();
  const debouncedValue = useDebounce(width, 500);
  useOutsideClick({ ref: titleRef, handler: onClose });

  useEffect(() => {
    if (titleRef.current) {
      const { scrollHeight, clientHeight, offsetWidth } = titleRef.current;
      setIsTitleOverflow(scrollHeight > clientHeight);
      isFullWidth && setTitleWidth(offsetWidth);
    }
  }, [title, debouncedValue, isFullWidth]);

  return (
    <Tooltip
      hasArrow
      isOpen={isOpen}
      label={title}
      bgColor="ourBlack"
      borderRadius="8px"
      sx={isFullWidth && { width: '100%', maxW: `${titleWidth}px` }}
      {...tooltipProps}
    >
      <InterText
        ref={titleRef}
        dangerouslySetInnerHTML={{ __html: title }}
        noOfLines={2}
        onClick={() => isTitleOverflow && onToggle()}
        {...(isTablet && {
          onMouseEnter: () => isTitleOverflow && onOpen(),
          onMouseLeave: () => isTitleOverflow && onClose(),
        })}
        {...titleProps}
      />
    </Tooltip>
  );
};

export default memo(TooltipText);
